import { RESTRICTED_ATTRIBUTES, RESTRICTED_EVENT_ATTRIBUTES } from '@/utils/attributes';
import type { IdentifierConfig } from './display-name';

/** Pure classification helpers for the JSON formatter (identifiers, reserved keys, timestamps, urls). */

export { MIN_UNIX_TIMESTAMP, MAX_UNIX_TIMESTAMP, isValidTimestamp, isUrl } from '@/utils/json-values';

const OBJECT_IDENTIFIER_KEYS = ['cio_object_id', 'object_id'];

/** Types whose root keys can be identifiers. */
const IDENTIFIER_LOG_TYPES = ['attribute_change', 'profile_create', 'profile_delete', 'profile_merge', 'segment_build'];

/** Whether this log type surfaces "Identifier" badges (keyed off the raw `log.type`). */
export function containsIdentifiersForLogType(type: string): boolean {
  return IDENTIFIER_LOG_TYPES.includes(type);
}

/** Reserved-attribute list for this log type. */
export function reservedAttributesForLogType(type: string): readonly string[] {
  if (type === 'attribute_change') return RESTRICTED_ATTRIBUTES;
  if (type === 'event') return RESTRICTED_EVENT_ATTRIBUTES;
  return [];
}

/** Whether an attribute name is an identifier (identifier config first, object keys as fallback). */
export function isIdentifierAttr(name: string, identifiers: IdentifierConfig): boolean {
  if (name in identifiers) return identifiers[name] !== 0;
  return OBJECT_IDENTIFIER_KEYS.includes(name);
}

/**
 * Whether an array belongs to a segment-membership change — its numeric elements are segment ids
 * that should render as segment-name links (keyed off the parent key being `entered`/`exited`).
 */
export function isSegmentMembershipArrayKey(parentKey: string | undefined): boolean {
  return parentKey === 'entered' || parentKey === 'exited';
}

/**
 * Coerce a segment-membership array element to its numeric segment id. Ids may serialize as a
 * number or a numeric string; both should link (matching the `Number(...)` resolution used when
 * the row's segment is looked up). Returns null for anything non-numeric so it falls back to a
 * plain JSON value.
 */
export function toSegmentId(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}
